import { useEffect, useRef, useState } from "react";
import { Badge } from "@/shared/components/ui/badge";
import { cn } from "@/shared/lib/utils";
import { assistantName } from "../lib/assistant-name";
import type { Assistant, Session, Snapshot } from "../types";

const connectionCopy: Record<string, string> = {
  Connected: "Up to date",
  Connecting: "Connecting…",
  "Catching up": "Catching up…",
  "Updates delayed": "Updates delayed · Your draft is saved in this tab",
  Offline: "Offline · Your draft is saved in this tab",
  "Sign in to reconnect": "Sign in to reconnect",
  "Chat access unavailable": "Chat access unavailable",
  "Local UI fixture": "Local UI fixture",
};

const degradedConnections = new Set([
  "Updates delayed",
  "Offline",
  "Sign in to reconnect",
  "Chat access unavailable",
]);

/** One contextual live region. The transcript stays browsable without treating
 * pagination, edits and receipt updates as newly received messages. */
export function ChatStatus({
  connection,
  online = true,
  open = true,
  snapshot,
  memberId,
  searching = false,
  typing = "",
  assistants,
  session,
}: {
  connection: string;
  online?: boolean;
  open?: boolean;
  snapshot?: Snapshot;
  memberId?: string;
  searching?: boolean;
  typing?: string;
  assistants?: Pick<Assistant, "memberId" | "name" | "isDefault">[];
  session?: Pick<Session, "assistant">;
}) {
  const [announcement, setAnnouncement] = useState("");
  const announced = useRef({ status: "", activity: "", open });
  const announcementTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const highWater = useRef<bigint | null>(null);
  const state = online ? connection : "Offline";
  const status = connectionCopy[state] ?? connection;
  const name = assistantName(snapshot?.participants, assistants, session);
  const activity =
    typing ||
    (snapshot?.workStatus === "QUEUED"
      ? `${name} reply queued`
      : snapshot?.workStatus === "WORKING"
        ? `${name} is working on your reply`
        : "");
  useEffect(() => () => clearTimeout(announcementTimer.current), []);
  useEffect(() => {
    const previousState = announced.current;
    let next = status !== previousState.status ? status : "";
    if (open && !previousState.open) next = status;
    if (
      activity &&
      activity !== previousState.activity &&
      !snapshot?.muted &&
      !searching
    )
      next = [next, activity].filter(Boolean).join(". ");
    announced.current = { status, activity, open };
    if (snapshot) {
      const current = BigInt(snapshot.lastNoticeSeq);
      const previous = highWater.current;
      highWater.current =
        previous === null || current > previous ? current : previous;
      if (
        previous !== null &&
        current > previous &&
        !searching &&
        !snapshot.muted
      ) {
        const received = snapshot.messages.filter(
          (message) =>
            !message.deleted &&
            message.memberId !== memberId &&
            BigInt(message.noticeSeq) > previous,
        );
        if (received.length) {
          const newest = received.at(-1)!;
          const mention = newest.mentions.some(
            (person) => person.memberId === memberId,
          )
            ? "You were mentioned. "
            : "";
          const incoming =
            received.length === 1
              ? `${mention}New message from ${newest.memberName}: ${newest.text.slice(0, 180) || "Shared media"}`
              : `${received.length} new messages. ${mention}Latest from ${newest.memberName}: ${newest.text.slice(0, 180) || "Shared media"}`;
          next = [next, incoming].filter(Boolean).join(". ");
        }
      }
    }
    if (!next || !open || document.visibilityState !== "visible") return;
    clearTimeout(announcementTimer.current);
    // Only the changed context is announced; typing and receipts do not replay
    // the previous incoming message. Snapshot refreshes do not cancel it.
    announcementTimer.current = setTimeout(() => setAnnouncement(next), 0);
  }, [snapshot, open, searching, memberId, status, activity]);
  return (
    <>
      <footer
        className={cn(
          "flex shrink-0 items-center bg-card px-3 py-1.5",
          state === "Connected" && "sr-only",
        )}
      >
        <Badge
          variant={degradedConnections.has(state) ? "destructive" : "secondary"}
          className="max-w-full gap-1.5 font-normal whitespace-normal"
        >
          <span
            aria-hidden="true"
            className="size-1.5 rounded-full bg-current"
          />
          {status}
        </Badge>
      </footer>
      <div
        className="sr-only"
        role="status"
        aria-live={open ? "polite" : "off"}
        aria-atomic="true"
      >
        {announcement}
      </div>
    </>
  );
}
